/**
 * Button — the one primitive every action uses.
 *
 * Variants:
 *  - `primary`  — filled, Spark accent (reserved for the principal CTA)
 *  - `secondary` — outlined, ink on transparent
 *  - `ghost`    — no border, for toolbars / inline actions
 *  - `danger`   — destructive action (deprecate / revoke)
 *
 * Sizes: `sm`, `md` (default), `lg`.
 *
 * Every variant has visible focus-ring via global `:focus-visible`;
 * disabled state dims + blocks pointer events + narrates to screen readers.
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/utils/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leading?: ReactNode;
  trailing?: ReactNode;
  /** Indicates the button owns a coming-soon action; renders a soft
   *  disabled state with a "Coming soon" badge next to the label. */
  comingSoon?: boolean;
}

const base =
  'inline-flex items-center justify-center gap-2 font-medium rounded-md ' +
  'transition-colors transition-shadow duration-150 ease-emphasized ' +
  'select-none whitespace-nowrap ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none';

const sizes: Record<ButtonSize, string> = {
  sm: 'text-xs h-8 px-3',
  md: 'text-sm h-10 px-4',
  lg: 'text-base h-12 px-5',
};

const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-spark text-white shadow-subtle hover:bg-spark-hover active:translate-y-px',
  secondary:
    'bg-surface text-ink border border-border hover:border-border-strong hover:bg-surface-raised',
  ghost: 'bg-transparent text-ink hover:bg-surface-raised',
  danger:
    'bg-surface text-danger border border-border hover:border-danger hover:bg-surface-raised',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    leading,
    trailing,
    comingSoon,
    className,
    children,
    disabled,
    ...rest
  },
  ref,
) {
  const isDisabled = Boolean(disabled) || comingSoon;
  return (
    <button
      ref={ref}
      className={cn(base, sizes[size], variants[variant], className)}
      disabled={isDisabled}
      aria-disabled={isDisabled || undefined}
      {...rest}
    >
      {leading ? <span className="flex h-4 w-4 items-center justify-center">{leading}</span> : null}
      <span>{children}</span>
      {comingSoon ? (
        <span className="rounded bg-surface-raised px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">
          soon
        </span>
      ) : null}
      {trailing ? <span className="flex h-4 w-4 items-center justify-center">{trailing}</span> : null}
    </button>
  );
});
